import { useCallback, useMemo } from 'react';
import authService from '@services/authService';
import { useAuthStore } from '@store/authStore';

/**
 * Reactive signed-in status for public contest pages.
 *
 * Important: do NOT call checkAuth() here. checkAuth clears the store when
 * hasActiveSession() is briefly false, which redirected players to /login
 * when opening View Contests (/player/contests/:sport).
 */
export function useActiveSession() {
  const storeUser = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const signedIn = useMemo(() => {
    if (authService.hasActiveSession()) return true;
    // Trust hydrated zustand session (token + user already validated at login)
    return Boolean(isAuthenticated && storeUser?.id);
  }, [isAuthenticated, storeUser]);

  const user = signedIn ? authService.getStoredUser() || storeUser : null;

  const refresh = useCallback(() => {
    // Heal store upward if localStorage has a valid session but store lagged
    if (authService.hasActiveSession() && !useAuthStore.getState().isAuthenticated) {
      useAuthStore.getState().checkAuth();
    }
    return authService.hasActiveSession() || Boolean(useAuthStore.getState().isAuthenticated);
  }, []);

  return {
    ready: true,
    signedIn,
    user,
    refresh,
  };
}
